import type { JsonObject } from "@/domains/common/types";
import { getMissingAudioContentFailureInfo } from "@/store/audio-recordings/app-events";
import {
  getAudioRecordingStrategy,
  type AudioRecordingModelData,
} from "@/store/audio-recordings/types";

export type AudioRecordingArtifactAvailability =
  | "available"
  | "uploadable"
  | "missing"
  | "empty"
  | "empty-or-missing"
  | "not-uploadable";

type BuildAudioRecordingArtifactDiagnosticsArgs = {
  diagnosticsSource: string;
  audioRecordingId: string;
  modelData: AudioRecordingModelData;
  artifactAvailability: AudioRecordingArtifactAvailability;
  artifactSizeBytes?: number | null;
  artifactDurationMs?: number | null;
  artifactPartCount?: number | null;
  artifactMimeType?: string | null;
  hasUploadableArtifact?: boolean | null;
  extraInfo?: JsonObject;
};

export type AudioRecordingArtifactDiagnostics = JsonObject & {
  diagnosticsSource: string;
  audioRecordingId: string;
  associatedNoteId: string | null;
  recordingStrategy: string;
  recordingStatus: string | null;
  uploadStatus: string | null;
  processingStatus: string | null;
  retranscriptionStatus: string | null;
  expectedUserErrorMessage: string;
  isTooShort: boolean;
  derivedDurationSec: number | null;
  derivedDurationFormatted: string | null;
  derivedDurationError: string | null;
  artifactAvailability: AudioRecordingArtifactAvailability;
  artifactSizeBytes: number | null;
  artifactDurationMs: number | null;
  artifactDurationSec: number | null;
  artifactPartCount: number | null;
  artifactMimeType: string | null;
  hasUploadableArtifact: boolean | null;
};

export function buildAudioRecordingArtifactDiagnostics({
  diagnosticsSource,
  audioRecordingId,
  modelData,
  artifactAvailability,
  artifactSizeBytes = null,
  artifactDurationMs = null,
  artifactPartCount = null,
  artifactMimeType = null,
  hasUploadableArtifact = null,
  extraInfo,
}: BuildAudioRecordingArtifactDiagnosticsArgs): AudioRecordingArtifactDiagnostics {
  const missingAudioFailureInfo = getMissingAudioContentFailureInfo({ modelData });
  const { durationResult, errorMessage, isTooShort } = missingAudioFailureInfo;

  return {
    diagnosticsSource,
    audioRecordingId,
    associatedNoteId: modelData.associated_note_id ?? null,
    recordingStrategy: getAudioRecordingStrategy({ modelData }),
    recordingStatus: modelData.recording_status ?? null,
    uploadStatus: modelData.upload_status ?? null,
    processingStatus: modelData.processing_status ?? null,
    retranscriptionStatus: modelData.retranscription_status ?? null,
    expectedUserErrorMessage: errorMessage,
    isTooShort,
    derivedDurationSec: durationResult.success
      ? Math.round(durationResult.duration.as("seconds"))
      : null,
    derivedDurationFormatted: durationResult.success
      ? durationResult.duration.toFormat("h:mm:ss")
      : null,
    derivedDurationError: durationResult.success ? null : durationResult.reason,
    artifactAvailability,
    artifactSizeBytes,
    artifactDurationMs,
    artifactDurationSec:
      typeof artifactDurationMs === "number" ? Math.round(artifactDurationMs / 1000) : null,
    artifactPartCount,
    artifactMimeType,
    hasUploadableArtifact,
    ...extraInfo,
  };
}
